/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 *
 * Project Insights: Google Health data for the project's participants, with a participant picker.
 * (Replaces the earlier demo charts and the Fitbit-era Looker Studio view.)
 */
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { functions } from '../../firebase';
import HealthInsights from './HealthInsights';

function InsightsPanel({ project }) {
  const [participants, setParticipants] = useState(null);
  const [selected, setSelected] = useState(null); // Set of ids; null until participants load
  const [applied, setApplied] = useState(null); // ids actually queried
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    httpsCallable(functions, 'getProjectParticipants')({ projectId: project.id })
      .then((r) => {
        if (!live) return;
        const list = (r.data?.participants || []).map((p) => ({
          id: p.uid || p.id,
          connected: p.health?.connected === true,
          status: p.health?.status || null,
        }));
        setParticipants(list);
        const all = new Set(list.map((p) => p.id));
        setSelected(all);
        setApplied([...all]);
      })
      .catch(() => { if (live) { setParticipants([]); setSelected(new Set()); setApplied([]); } });
    return () => { live = false; };
  }, [project.id]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const changed = applied && selected && (applied.length !== selected.size || applied.some((id) => !selected.has(id)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <span className="ja-label">insights</span>
        <h3 style={{ fontSize: 18, fontWeight: 500, margin: '0 0 4px', color: 'var(--jp-gray-800)' }}>{project?.name}</h3>
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h4 className="ja-card-title">Participants</h4>
          <span className="ja-card-meta">
            {selected ? `${selected.size} of ${participants.length} selected` : 'loading…'}
          </span>
        </div>
        {participants?.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', margin: 0 }}>No registered participants yet.</p>
        )}
        {participants?.length > 0 && (
          <>
            <div className="ja-btn-row" style={{ alignItems: 'center' }}>
              <button type="button" className="ja-btn ja-btn-ghost" onClick={() => setOpen(!open)}>
                {open ? '▲ Hide list' : '▼ Choose participants'}
              </button>
              <button type="button" className="ja-btn ja-btn-ghost" onClick={() => setSelected(new Set(participants.map((p) => p.id)))}>All</button>
              <button type="button" className="ja-btn ja-btn-ghost" onClick={() => setSelected(new Set(participants.filter((p) => p.connected).map((p) => p.id)))}>Connected only</button>
              <button type="button" className="ja-btn ja-btn-ghost" onClick={() => setSelected(new Set())}>None</button>
              <button type="button" className="ja-btn ja-btn-primary" disabled={!changed || selected.size === 0}
                onClick={() => setApplied([...selected])}>
                Apply
              </button>
            </div>
            {open && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 4, marginTop: 12 }}>
                {participants.map((p) => (
                  <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    <span style={{ fontFamily: 'var(--jp-mono)' }}>{p.id}</span>
                    {!p.connected && <span className="ja-chip ja-chip-inactive">{p.status || 'not connected'}</span>}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {applied && applied.length > 0 && (
        <HealthInsights projectId={project.id} participantIds={applied} />
      )}
    </div>
  );
}

InsightsPanel.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

export default InsightsPanel;
